import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { BarChart2, TrendingUp, Users, CreditCard } from 'lucide-react';
import CashFlowReport from '../components/reports/CashFlowReport';
import CollectionDebtReport from '../components/reports/CollectionDebtReport';
import TeamPerformanceReport from '../components/reports/TeamPerformanceReport';
import SpecialPaymentsReport from '../components/reports/SpecialPaymentsReport';
import { ReportPeriodType, ReportFilterDto } from '../services/reportsService';
import { useAuth } from '../hooks/useAuth';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const PageSubtitle = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
`;

const Select = styled.select`
  padding: 8px 14px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 150px;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }

  @media (max-width: 767px) {
    min-width: 130px;
    font-size: 13px;
  }
`;

const DateInput = styled.input<{ $hasValue?: boolean }>`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: white;
  transition: all 0.2s ease;
  width: 140px;
  direction: ltr;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }

  &::-webkit-datetime-edit-text,
  &::-webkit-datetime-edit-month-field,
  &::-webkit-datetime-edit-day-field,
  &::-webkit-datetime-edit-year-field {
    color: ${props => props.$hasValue ? 'var(--text-primary)' : 'transparent'};
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
  }

  @media (max-width: 767px) {
    width: 120px;
    font-size: 13px;
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 12px;
  padding-right: 12px;
  border-right: 1px solid var(--border-color);

  @media (max-width: 767px) {
    margin-right: 0;
    padding-right: 0;
    border-right: none;
    margin-top: 12px;
    width: 100%;
    justify-content: flex-start;
  }
`;

const DateSeparator = styled.span`
  color: var(--text-secondary);
  font-size: 13px;
`;

const TabsContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-light);
  overflow: hidden;
`;

const TabHeaders = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
    gap: 12px;
  }
`;

const TabsRow = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  flex: 1;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 3px;
  }

  @media (max-width: 767px) {
    gap: 8px;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    flex-wrap: wrap;
    width: 100%;
    justify-content: flex-start;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  ${props => props.$active ? `
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
    box-shadow: 0 2px 4px rgba(0, 142, 162, 0.2);

    svg {
      color: white;
    }
  ` : `
    background: white;
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover {
      border-color: var(--primary-color);
      color: var(--primary-color);
    }

    svg {
      color: var(--text-secondary);
    }

    &:hover svg {
      color: var(--primary-color);
    }
  `}

  @media (max-width: 767px) {
    padding: 8px 14px;
    font-size: 13px;
  }
`;

const TabContent = styled.div`
  padding: 24px;
  min-height: 400px;
  background: white;
`;

type TabKey = 'cash-flow' | 'collection-debt' | 'team-performance' | 'special-payments';

const validTabs: TabKey[] = ['cash-flow', 'collection-debt', 'team-performance', 'special-payments'];

const ReportsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSystemAdmin, isSecretary } = useAuth();

  // מזכירה (שאינה מנהל מערכת) — רואה אך ורק את דוח הגביה וחובות
  const secretaryOnly = isSecretary && !isSystemAdmin;

  // Get active tab from URL or default
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = secretaryOnly
    ? 'collection-debt'
    : validTabs.includes(tabParam as TabKey)
      ? (tabParam as TabKey)
      : 'cash-flow';

  const [filter, setFilter] = useState<ReportFilterDto>({
    periodType: ReportPeriodType.Month,
    dateFrom: undefined,
    dateTo: undefined
  });

  const handleTabChange = (tabKey: TabKey) => {
    setSearchParams({ tab: tabKey });
  };

  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const periodType = parseInt(e.target.value) as ReportPeriodType;
    setFilter({
      ...filter,
      periodType,
      dateFrom: periodType === ReportPeriodType.Custom ? filter.dateFrom : undefined,
      dateTo: periodType === ReportPeriodType.Custom ? filter.dateTo : undefined
    });
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({ ...filter, dateFrom: e.target.value });
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({ ...filter, dateTo: e.target.value });
  };

  const allTabs: { key: TabKey; title: string; icon: typeof BarChart2 }[] = [
    { key: 'cash-flow', title: 'תזרים מזומנים', icon: TrendingUp },
    { key: 'collection-debt', title: 'גביה וחובות', icon: CreditCard },
    { key: 'team-performance', title: 'ביצועי צוות', icon: Users },
    { key: 'special-payments', title: 'תשלומים מיוחדים', icon: BarChart2 }
  ];

  // מזכירה רואה רק את טאב הגביה וחובות
  const tabs = secretaryOnly
    ? allTabs.filter(t => t.key === 'collection-debt')
    : allTabs;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'cash-flow':
        return <CashFlowReport filter={filter} />;
      case 'collection-debt':
        return <CollectionDebtReport filter={filter} hideSummaryCards={secretaryOnly} />;
      case 'team-performance':
        return <TeamPerformanceReport filter={filter} />;
      case 'special-payments':
        return <SpecialPaymentsReport filter={filter} />;
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRight>
          <div>
            <PageTitle>דוחות ניהוליים</PageTitle>
            <PageSubtitle>דוחות מפורטים על תזרים מזומנים, גביה וחובות, וביצועי הצוות</PageSubtitle>
          </div>
        </HeaderRight>
      </PageHeader>

      <TabsContainer>
        <TabHeaders>
          <TabsRow>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Tab
                  key={tab.key}
                  $active={activeTab === tab.key}
                  onClick={() => handleTabChange(tab.key)}
                >
                  <Icon size={16} />
                  {tab.title}
                </Tab>
              );
            })}
          </TabsRow>
          <FiltersRow>
            {filter.periodType === ReportPeriodType.Custom && (
              <DateRangeContainer>
                <DateInput
                  type="date"
                  value={filter.dateFrom || ''}
                  onChange={handleDateFromChange}
                  $hasValue={!!filter.dateFrom}
                />
                <DateSeparator>עד</DateSeparator>
                <DateInput
                  type="date"
                  value={filter.dateTo || ''}
                  onChange={handleDateToChange}
                  $hasValue={!!filter.dateTo}
                />
              </DateRangeContainer>
            )}
            <Select value={filter.periodType} onChange={handlePeriodChange}>
              <option value={ReportPeriodType.Month}>חודש נוכחי</option>
              <option value={ReportPeriodType.Quarter}>רבעון נוכחי</option>
              <option value={ReportPeriodType.Year}>שנה נוכחית</option>
              <option value={ReportPeriodType.Custom}>טווח מותאם אישית</option>
            </Select>
          </FiltersRow>
        </TabHeaders>
        <TabContent>
          {renderTabContent()}
        </TabContent>
      </TabsContainer>
    </PageContainer>
  );
};

export default ReportsPage;
